/**
 * PassportLearningCenter
 * 
 * Learning Center section for the Technician Passport.
 * Phase 0 implementation with guardrails:
 * - Max 3 recommendations with caps
 * - Provider attribution
 * - Free/Paid badges
 * - "Why you're seeing this" explanations
 * - All links open externally
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  BookOpen, 
  GraduationCap,
  History,
  Sparkles,
  Loader2,
  User,
  BarChart3,
  ClipboardCheck,
  Search,
  Info,
  Library,
} from 'lucide-react';
import { useSmartRecommendations } from '@/hooks/useSmartRecommendations';
import { useLearningHistory, useTrackLearningClick } from '@/hooks/useLearningCenter';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { TrainingRecommendationCard } from './TrainingRecommendationCard';
// Batch 18-G19 — Passport-specific display adapter that maps the
// existing `useSmartRecommendations` `whyShowing` clauses into a
// bounded reason label / fragment + the renamed content-quality
// `OptiBay reviewed content` chip. See the G18 plan for the
// rationale; the helper itself does NOT consume requirements or
// evidence data and does NOT call the ticket ranker.
import { buildPassportRecommendationDisplayState } from '@/utils/training/passportRecommendationDisplayState';
import { TrainingConsumptionConfirmDialog } from '@/components/training/TrainingConsumptionConfirmDialog';
import { BookProgressDialog } from '@/components/training/BookProgressDialog';
import { useSessionsNeedingConfirmation, useBookSessionsNeedingProgress } from '@/hooks/useTrainingConsumption';
import { 
  ARTIFACT_TYPE_ICONS,
  type LearningIntent,
  type TrainingArtifact,
} from '@/types/training';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Catalyst-only tabs
import { LearningProfileTab } from './LearningProfileTab';
import { LearningImpactTab } from './LearningImpactTab';
import { LearningRequirementsTab } from './LearningRequirementsTab';
import { BrowseCatalogTab } from './BrowseCatalogTab';
import { ScheduleAwareRecommendations } from './ScheduleAwareRecommendations';
import { LearningLibraryTab } from './LearningLibraryTab';
import { MonitoredTrainingHistoryList } from './MonitoredTrainingHistoryList';
import type { LearningHistoryStages } from '@/lib/training/learningHistoryStages';
// K108-X — owner/admin Needs Review queue (self-gates to manager/owner/admin; null otherwise).
import { TrainingNeedsReviewQueue } from '@/components/training/TrainingNeedsReviewQueue';

interface PassportLearningCenterProps {
  technicianProfileId: string;
  proficiencies?: Array<{ category_key: string }>;
  isOwnProfile?: boolean;
}

/**
 * Core-100 R4 wave 2 CORE-O: one history entry's stages, kept apart — opened, engaged, progress, completed — and how
 * completion was determined. Never "completed training" on its own; never proficiency or certification.
 */
export function LearningHistoryStageLine({ stages }: { stages: LearningHistoryStages }) {
  const day = (iso: string | null) => (iso ? format(new Date(iso), 'MMM d, yyyy') : null);
  return (
    <div className="mt-1 space-y-0.5" data-testid="history-entry-stages">
      <p className="text-xs text-muted-foreground">
        <span data-testid="stage-opened">Opened {day(stages.openedAt) ?? '—'}</span>
        {' · '}
        <span data-testid="stage-engaged">{stages.engagedAt ? `Engaged ${day(stages.engagedAt)}` : 'No engagement observed'}</span>
        {stages.progressPercent != null && (
          <>{' · '}<span data-testid="stage-progress">Progress {stages.progressPercent}%</span></>
        )}
        {' · '}
        <span data-testid="stage-completed">
          {stages.completed
            ? `Completed ${day(stages.completedAt) ?? ''}`.trim()
            : (stages.endedWithout ?? 'Not completed')}
        </span>
      </p>
      {stages.determinationLabel && (
        <p className="text-xs text-muted-foreground" data-testid="stage-determined">How completion was determined: {stages.determinationLabel}</p>
      )}
    </div>
  );
}

export function PassportLearningCenter({ 
  technicianProfileId,
  proficiencies = [],
  isOwnProfile = false,
}: PassportLearningCenterProps) {
  const isAdvanced = useFeatureFlag('learningCenterAdvanced');
  const hasIntelligence = useFeatureFlag('learningCenter_intelligence');
  const hasEffectiveness = useFeatureFlag('learningCenter_effectiveness');
  const hasRequirements = useFeatureFlag('learningCenter_requirements');
  
  // Consumption confirmation state
  const [activeTab, setActiveTab] = useState('foundational');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showBookDialog, setShowBookDialog] = useState(false);
  
  // Check for pending confirmations when returning to Foundational tab
  const { data: pendingConfirmations } = useSessionsNeedingConfirmation(technicianProfileId);
  const { data: bookSessions } = useBookSessionsNeedingProgress(technicianProfileId);
  
  // Show confirmation dialog when switching to foundational tab with pending sessions
  useEffect(() => {
    if (activeTab === 'foundational' && pendingConfirmations && pendingConfirmations.length > 0) {
      setShowConfirmDialog(true);
    }
  }, [activeTab, pendingConfirmations]);
  
  // Show book progress dialog if there are books needing check-in
  useEffect(() => {
    if (activeTab === 'foundational' && bookSessions && bookSessions.length > 0 && !showConfirmDialog) {
      setShowBookDialog(true);
    }
  }, [activeTab, bookSessions, showConfirmDialog]);
  
  // Get domains from proficiencies for filtering
  const domains = proficiencies.map(p => p.category_key);
  
  // Use smart recommendations with Phase 0 guardrails
  const { 
    recommendations, 
    isLoading: loadingRecommended,
    meta 
  } = useSmartRecommendations({
    technicianProfileId,
    targetDomains: isAdvanced ? domains : [],
  });
  
  const { data: history, isLoading: loadingHistory } = useLearningHistory(technicianProfileId);
  const trackClick = useTrackLearningClick();

  const handleArtifactClick = (artifact: TrainingArtifact, intent?: LearningIntent) => {
    // Track the click
    trackClick.mutate({
      technicianProfileId,
      artifactId: artifact.id,
      source: 'learning_center',
      intent: intent || undefined,
    });
    // Navigation handled by anchor tag (GUARDRAIL: always link out, never embed)
  };

  // Determine which tabs to show
  const showCatalystTabs = isAdvanced || hasIntelligence || hasEffectiveness || hasRequirements;

  return (
    <Card data-testid="passport-learning-center">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <GraduationCap className="h-5 w-5 text-primary" />
          Learning Center
          {isAdvanced && (
            <Badge variant="secondary" className="ml-2 text-xs">
              <Sparkles className="h-3 w-3 mr-1" />
              Personalized
            </Badge>
          )}
          {/* Show guardrail info for debugging */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help ml-auto" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <p className="text-xs">
                  Showing {meta.afterCaps} of {meta.totalCandidates} candidates.
                  Max 3 total, 1 per topic, 1 per provider, 1 paid.
                  {meta.isWorkHours && ' Books suppressed (work hours).'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* K108-X: owner/admin Training Needs Review queue — renders null for
            technicians and when there is nothing unresolved. */}
        <div className="mb-4">
          <TrainingNeedsReviewQueue />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={cn(
            "grid w-full",
            showCatalystTabs ? "grid-cols-4 md:grid-cols-7" : "grid-cols-3"
          )}>
            <TabsTrigger value="foundational" className="gap-1 text-xs">
              <Sparkles className="h-3 w-3" />
              <span className="hidden sm:inline">Foundational</span>
              <span className="sm:hidden">Learn</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-1 text-xs">
              <History className="h-3 w-3" />
              <span className="hidden sm:inline">History</span>
              <span className="sm:hidden">Past</span>
            </TabsTrigger>
            <TabsTrigger value="library" className="gap-1 text-xs">
              <Library className="h-3 w-3" />
              <span className="hidden sm:inline">Library</span>
              <span className="sm:hidden">All</span>
            </TabsTrigger>
            {showCatalystTabs && (
              <>
                <TabsTrigger value="browse" className="gap-1 text-xs">
                  <Search className="h-3 w-3" />
                  <span className="hidden sm:inline">Browse</span>
                  <span className="sm:hidden">All</span>
                </TabsTrigger>
                {hasIntelligence && (
                  <TabsTrigger value="profile" className="gap-1 text-xs">
                    <User className="h-3 w-3" />
                    <span className="hidden sm:inline">Profile</span>
                    <span className="sm:hidden">Me</span>
                  </TabsTrigger>
                )}
                {hasEffectiveness && (
                  <TabsTrigger value="impact" className="gap-1 text-xs">
                    <BarChart3 className="h-3 w-3" />
                    <span className="hidden sm:inline">Impact</span>
                    <span className="sm:hidden">Stats</span>
                  </TabsTrigger>
                )}
                {hasRequirements && (
                  <TabsTrigger value="requirements" className="gap-1 text-xs">
                    <ClipboardCheck className="h-3 w-3" />
                    <span className="hidden sm:inline">Required</span>
                    <span className="sm:hidden">Req</span>
                  </TabsTrigger>
                )}
              </>
            )}
          </TabsList>

          <TabsContent value="foundational" className="mt-4">
            {!isAdvanced && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-dashed">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Upgrade to <strong>OptiBay Catalyst</strong> for personalized learning recommendations based on skills and performance.
                </p>
              </div>
            )}

            {/* Phase 4: Schedule-aware recommendations for advanced users */}
            {isAdvanced ? (
              <ScheduleAwareRecommendations technicianProfileId={technicianProfileId} canOpen={isOwnProfile} />
            ) : loadingRecommended ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : recommendations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No training resources available yet.</p>
                <p className="text-sm mt-1">Check back soon for foundational learning.</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {recommendations.map((scored) => {
                    // Batch 18-G19 — Passport-specific display
                    // adapter. Maps `whyShowing` into a bounded
                    // reason label + optional second-line
                    // fragment, and renames the existing
                    // content-quality chip via the
                    // `displayBadges` array. Order, scoring, and
                    // Phase-0 guardrails are all owned by
                    // `useSmartRecommendations`; the helper only
                    // shapes display state.
                    const displayState =
                      buildPassportRecommendationDisplayState({
                        whyShowing: scored.whyShowing,
                        isOptibayVerifiedContent:
                          scored.artifact?.confidence_level ===
                          'optibay_verified',
                        isLive:
                          (scored.artifact as any)?.delivery_format ===
                          'live',
                        isReferenceOnly: Boolean(
                          (scored.artifact as any)?.artifact_type === 'book' &&
                            (scored.artifact as any)?.is_reference_only,
                        ),
                      });
                    return (
                      <TrainingRecommendationCard
                        key={scored.artifact.id}
                        artifact={scored.artifact as any}
                        whyShowing={scored.whyShowing}
                        displayState={displayState}
                        onOpen={(intent) =>
                          handleArtifactClick(scored.artifact as any, intent)
                        }
                        showActions={false}
                        technicianProfileId={technicianProfileId}
                        surface="foundational"
                        canOpen={isOwnProfile}
                      />
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4 space-y-4">
            {/* K108-R Batch 4: launched/monitored training sessions (observed vs
                reported), above the click log. Renders null when there are no
                sessions; degrades gracefully when extension evidence is missing. */}
            <MonitoredTrainingHistoryList technicianProfileId={technicianProfileId} />
            <p className="text-xs text-muted-foreground" data-testid="history-stage-note">
              Opening, engaging with and completing training are recorded separately. Completing training is not a certification or a measure of skill.
            </p>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !history || history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>No learning history yet.</p>
                <p className="text-sm mt-1">Training accessed will appear here.</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-2">
                  {history.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-lg flex-shrink-0">
                          {ARTIFACT_TYPE_ICONS[entry.artifact?.artifact_type || 'article']}
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium truncate">
                            {entry.artifact?.title || 'Unknown Training'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {entry.artifact?.provider_name} • {format(new Date(entry.clicked_at), 'MMM d, yyyy h:mm a')}
                            {entry.artifact_is_current === false && <span data-testid="history-entry-retired"> • No longer offered</span>}
                          </p>
                          {entry.stages && <LearningHistoryStageLine stages={entry.stages} />}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {entry.intent && (
                          <Badge variant="outline" className="text-xs">
                            {entry.intent.replace('_', ' ')}
                          </Badge>
                        )}
                        <Badge variant="secondary" className="text-xs capitalize">
                          {entry.source.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          {/* Library tab - available to all users */}
          <TabsContent value="library" className="mt-4">
            <LearningLibraryTab technicianProfileId={technicianProfileId} canOpen={isOwnProfile} />
          </TabsContent>

          {/* Catalyst-only tabs */}
          {showCatalystTabs && (
            <>
              <TabsContent value="browse" className="mt-4">
                <BrowseCatalogTab technicianProfileId={technicianProfileId} canOpen={isOwnProfile} />
              </TabsContent>

              {hasIntelligence && (
                <TabsContent value="profile" className="mt-4">
                  <LearningProfileTab 
                    technicianProfileId={technicianProfileId}
                    proficiencies={proficiencies}
                  />
                </TabsContent>
              )}

              {hasEffectiveness && (
                <TabsContent value="impact" className="mt-4">
                  <LearningImpactTab technicianProfileId={technicianProfileId} />
                </TabsContent>
              )}

              {hasRequirements && (
                <TabsContent value="requirements" className="mt-4">
                  <LearningRequirementsTab technicianProfileId={technicianProfileId} />
                </TabsContent>
              )}
            </>
          )}
        </Tabs>
        
        {/* Consumption Confirmation Dialogs */}
        {pendingConfirmations && pendingConfirmations.length > 0 && (
          <TrainingConsumptionConfirmDialog
            open={showConfirmDialog}
            onOpenChange={setShowConfirmDialog}
            session={pendingConfirmations[0]}
          />
        )}
        
        {bookSessions && bookSessions.length > 0 && (
          <BookProgressDialog
            open={showBookDialog}
            onOpenChange={setShowBookDialog}
            session={bookSessions[0]}
          />
        )}
      </CardContent>
    </Card>
  );
}

// Keep TrainingArtifactCard export for backwards compatibility
export { TrainingArtifactCard } from './TrainingArtifactCard';
